import { createSelector } from '@ngrx/store';
import { getLanguage, getLanguages } from '@cemex/cmx-client-layout';
import * as moment from 'moment/moment';
import DOMPurify from 'dompurify';

export const generalLangSelector = createSelector(getLanguage, getLanguages, (language, languages) => ({
  language,
  languages,
}));

export const getCurrentLanguageWithLocaleInfo = createSelector(generalLangSelector, ({ language, languages }) => {
  if (language == null || languages == null) {
    return undefined;
  }

  return languages.find((langObj) => langObj.languageISO === language);
});

export const getLabelFromMessageApi = (errorManager: any): string => {
  if (errorManager.moreInformation) {
    return errorManager.moreInformation;
  } else {
    return undefined;
  }
};

export enum BusinessDayType {
  NonWorkingDay = 'NonW',
  Holiday = 'Holi',
  Ok = 'Ok',
}

export const transformCoordinates = (lat: number, lng: number): { latitude: string; longitude: string } => {
  const latitude = lat < 0 ? `S: ${decimalToDegree(Math.abs(lat))}` : `N: ${decimalToDegree(lat)}`;
  const longitude = lng < 0 ? `W: ${decimalToDegree(Math.abs(lng))}` : `E: ${decimalToDegree(lng)}`;
  return { latitude, longitude };
};

export const transformCoordinatesShort = (lat: number, lng: number): { latitude: string; longitude: string } => {
  const latitude = lat < 0 ? `${decimalToDegreeShort(Math.abs(lat))}S` : `${decimalToDegreeShort(lat)}N`;
  const longitude = lng < 0 ? `${decimalToDegreeShort(Math.abs(lng))}W` : `${decimalToDegreeShort(lng)}E`;
  return { latitude, longitude };
};

function decimalToDegree(value: number) {
  const tmp = value.toString().split('.');

  const degrees = Math.abs(parseInt(tmp[0], 10));
  const minutes = (parseFloat(`0.${tmp[1]}`) || 0) * 60;
  const secondsPart = minutes.toString().split('.');

  const seconds = ((parseFloat(`0.${secondsPart[1]}`) || 0) * 60).toFixed(2);

  return `${degrees}° ${Math.floor(minutes)}′ ${seconds}″`;
}

function decimalToDegreeShort(value: number) {
  const tmp = value.toString().split('.');

  const degrees = Math.abs(parseInt(tmp[0], 10));
  const minutes = (parseFloat(`0.${tmp[1]}`) || 0) * 60;
  const secondsPart = minutes.toString().split('.');
  const seconds = ((parseFloat(`0.${secondsPart[1]}`) || 0) * 60).toFixed(1);
  return `${degrees}°${Math.floor(minutes)}′${seconds}″`;
}

/**
 * @param jsonData
 * @returns encoded object
 */
export function encodeJson(jsonData: any): any {
  let data = '';
  const find = '=';
  const re = new RegExp(find, 'g');
  data = btoa(JSON.stringify(jsonData));
  data = data.replace(re, '-');
  data = data.split('').reverse().join('');
  data = btoa(data);
  data = data.replace(re, '-');
  return data;
}

/**
 * Rounds the given time to the nearest 10 minutes.
 *
 * @param time - The moment object representing the time to be rounded.
 * @returns A new moment object rounded to the nearest 10 minutes.
 */
export function roundToNearestMinutes(time: moment.Moment, roundTo: number): moment.Moment {
  const minutes = time.minutes();
  const roundedMinutes = Math.ceil(minutes / roundTo) * roundTo;

  return time.clone().minutes(roundedMinutes).seconds(0).milliseconds(0);
}

/**
 * @param url string to validate
 * @returns boolean if url is valid
 * Validates the URL format, if it is a valid URL.
 */
export function isValidUrl(url: string): boolean {
  const urlPattern = /^(https?:\/\/[^\s/$.?#].[^\s]*)$/i;
  return urlPattern.test(url);
}

/**
 * @param url string to sanitize
 * @returns sanitized url
 * Sanitizes the URL using DOMPurify.
 */
export function sanitizeUrl(url: string): string {
  return DOMPurify.sanitize(url, { ALLOWED_URI_REGEXP: /^(https?|mailto|ftp):/ });
}

/**
 * @param encoded string to validate
 * @returns boolean if encoded data is safe
 * Validates that the encoded data is safe to use.
 * Basic check to avoid common dangerous characters
 */
export function isSafeEncodedData(encoded: string): boolean {
  return !/[<>]/.test(encoded);
}
